import { createContext, useContext, ReactNode, useMemo, useState, useEffect, useCallback, useRef } from "react";

import { useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { EntityRecord } from "@/types/entity";
import { ISwivuProject } from "@/types/SwivuProject";
import { isImageFileTyped } from "@/react/components/entityViews/files/components/FileHelpers";
import { formatFileMetadata } from "@/react/components/entityViews/files/utils/fileMetadataUtils";
import { MetadataRow } from "@/react/components/entityViews/files/utils/fileMetadataUtils";
import { TComboOption } from "@/modern/components/ui/combobox";
import { getFileExtension } from "@/modern/utils";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";

import { useEntityManager } from "@/react/hooks/useEntityManager";

import { useImageLoading } from "../../../../hooks/useImageLoading";
import { useFileNavigation } from "../hooks/useFileNavigation";
import { useFileSpotIncentive } from "../../../../hooks/useFileSpotIncentive";
import { useDocumentExtraction, ExtractionRequestBody } from "../components/extraction/hooks/useDocumentExtraction";
import { useDocumentTextCache } from "../components/extraction/hooks/useDocumentTextCache";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { usePdfTextSearch } from "../components/extraction/hooks/usePdfTextSearch";
import { ExtractionType, ExtractionState, EXTRACTION_OPERATIONS } from "../components/extraction/types";
import { useFileInsights } from "@/react/hooks/entityHooks/useFileInsights";
import { useFileUrl } from "@/react/components/entityViews/files/api/files.api";
import type { FileViewerMode } from "../types";
import { canNavigateFileCollection, isFocusedWorkspaceMode } from "../types";

const EXTRACTABLE_EXTENSIONS = new Set(["pdf", "xlsx", "xls", "docx", "csv", "tsv", "txt"]);

/** Feature tabs that force the right rail visible regardless of the collapsed pref. */
const RAIL_FEATURE_MODES: readonly FileViewerMode[] = [
  "summaryShare",
  "promote",
  "extract",
  "analyze",
  "adjustPoints",
];

export interface AnalyzeImageOverlayState {
  feedFilter: string | null;
  generatingPersonaIds: string[];
  scanPersonaId: string | null;
  scanPersonaName: string | null;
}

export const EMPTY_ANALYZE_IMAGE_OVERLAY: AnalyzeImageOverlayState = {
  feedFilter: null,
  generatingPersonaIds: [],
  scanPersonaId: null,
  scanPersonaName: null,
};

interface FileDetailModalContextValue {
  // File state
  file: SwivuFile | null;
  files: SwivuFile[];
  imageFile: SwivuFile | null;
  imageUrlToLoad: string | undefined;

  // Image loading state
  imageLoading: boolean;
  showSpinner: boolean;
  displayedImageUrl: string | undefined;
  currentImageUrl: string | undefined;
  handleLoadComplete: (loadedUrl?: string) => void;

  // Navigation state
  hasPrevious: boolean;
  hasNext: boolean;
  handlePrevious: () => void;
  handleNext: () => void;

  // File permissions
  canEditFile: boolean;
  handleFileDownload: (file: SwivuFile) => void;
  deleteFile: (id: number) => Promise<void>;

  // Spot incentive state
  shouldShowAdjustPoints: (file: SwivuFile | null) => boolean;
  handleAdjustPoints: (
    file: SwivuFile,
    isCreatingUnreleasedRecord?: boolean,
    onReviewClick?: () => void,
    extras?: { onFileDetailResume?: () => void },
  ) => Promise<void>;

  // Exclusive viewer mode
  viewerMode: FileViewerMode;
  setViewerMode: (mode: FileViewerMode) => void;

  // UI state (legacy tabs kept for SummaryShare panel sections)
  activeTab: "review" | "summary";
  setActiveTab: (tab: "review" | "summary") => void;
  summaryTab: "details" | "findings" | "points" | "share";
  setSummaryTab: (tab: "details" | "findings" | "points" | "share") => void;
  isAnnotating: boolean;
  setIsAnnotating: (value: boolean) => void;
  /** True when Promote / Analyze (docked) / Adjust Points — shell is near-fullscreen. */
  isFocusedWorkspace: boolean;
  /** @deprecated Alias of isFocusedWorkspace — kept for existing callers. */
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  /** Persisted preference: user collapsed the right rail. */
  isRailCollapsed: boolean;
  /** Effective visibility — always true for feature tabs; Details respects the pref. */
  isRailExpanded: boolean;
  toggleRailCollapsed: () => void;
  isAnalyzeDialogOpen: boolean;
  setIsAnalyzeDialogOpen: (open: boolean) => void;
  handleOpenAnalyzePanel: () => void;
  handleCloseAnalyzePanel: () => void;
  /** True when this file has at least one saved insight (analysis has been run). */
  isFileAnalyzed: boolean;
  isAnalyzePanelDetached: boolean;
  toggleAnalyzePanelDetach: () => void;
  /** Opens Summary & Share mode (e.g. from Analyze panel). */
  handleNavigateToSharePermissions: () => void;
  isSummarySharePanelOpen: boolean;
  handleOpenSummarySharePanel: () => void;
  handleCloseSummarySharePanel: () => void;
  isAdjustPointsPanelOpen: boolean;
  handleOpenAdjustPointsPanel: () => void;
  handleCloseAdjustPointsPanel: () => void;

  /** On-image analyze overlay (scan + glow); synced from Analyze panel. */
  analyzeImageOverlay: AnalyzeImageOverlayState;
  setAnalyzeImageOverlay: (
    patch:
      | Partial<AnalyzeImageOverlayState>
      | ((prev: AnalyzeImageOverlayState) => AnalyzeImageOverlayState),
  ) => void;

  // Modal state
  open: boolean;
  setOpen: (open: boolean) => void;

  // Computed values
  metadataRows: MetadataRow[];
  shouldShowSpotIncentiveButton: boolean;

  // External dependencies
  entityRecord: EntityRecord;
  project: ISwivuProject | null;
  swivuAppGrants: TComboOption[];
  editorHandler: any;
  handleFileOpen: (file: SwivuFile) => void;

  // Handlers
  handleAnnotating: () => void;
  handleCreateSpotIncentive: () => void;

  // Extraction panel state
  isExtractable: boolean;
  isExtractionPanelOpen: boolean;
  setIsExtractionPanelOpen: (open: boolean) => void;
  activeExtraction: ExtractionType | null;
  extractionStates: Partial<Record<ExtractionType, ExtractionState>>;
  handleRunExtraction: (type: ExtractionType) => void;
  handleRunAllExtractions: () => void;
  handleViewExtraction: (type: ExtractionType) => void;
  handleCloseExtractionPanel: () => void;
  handleBackToOperationList: () => void;

  // PDF search
  pdfSearchText: string | null;
  handleSearchInPdf: (searchText: string) => void;
  clearPdfSearch: () => void;

  // Entity link
  copyEntityLink: ReturnType<typeof useEntityManager>['copyEntityLink'];

  // Promote panel state
  isPromotePanelOpen: boolean;
  setIsPromotePanelOpen: (open: boolean) => void;
  handleOpenPromotePanel: () => Promise<void>;
  isFilePromoted: boolean;
  handleClosePromotePanel: () => void;
  promoteAiSuggestions: string[];
  setPromoteAiSuggestions: (suggestions: string[]) => void;
  promoteVisibilityWarnings: string[];
  setPromoteVisibilityWarnings: (warnings: string[]) => void;
}

const FileDetailModalContext = createContext<FileDetailModalContextValue | undefined>(undefined);

interface FileDetailModalProviderProps {
  children: ReactNode;
  entityRecord: EntityRecord;
  file: SwivuFile | null;
  navigationFiles?: SwivuFile[];
  open: boolean;
  setOpen: (open: boolean) => void;
  project: ISwivuProject | null;
  swivuAppGrants: TComboOption[];
  editorHandler: any;
  autoOpenPromotePanel?: boolean;
  initialViewerMode?: FileViewerMode;
}

export const FileDetailModalProvider = ({
  children,
  entityRecord,
  file,
  navigationFiles,
  open,
  setOpen,
  project,
  swivuAppGrants,
  editorHandler,
  autoOpenPromotePanel,
  initialViewerMode = "default",
}: FileDetailModalProviderProps) => {
  const [localFile, setLocalFile] = useState<SwivuFile | null>(file);
  const { files: contextFiles, handleFileOpen, deleteFile, handleFileDownload } = useEntityFileContext();
  const files = navigationFiles ?? contextFiles;
  const [activeTab, setActiveTab] = useState<"review" | "summary">("review");
  const [summaryTab, setSummaryTab] = useState<"details" | "findings" | "points" | "share">("share");
  const [viewerMode, setViewerModeState] = useState<FileViewerMode>(
    autoOpenPromotePanel ? "promote" : initialViewerMode,
  );
  /** Where Summary & Share Back should return (e.g. analyze → share → analyze). */
  const summaryShareReturnModeRef = useRef<FileViewerMode>("default");
  const [isAnalyzePanelDetached, setIsAnalyzePanelDetached] = useState(false);
  const [analyzeImageOverlay, setAnalyzeImageOverlayState] =
    useState<AnalyzeImageOverlayState>(EMPTY_ANALYZE_IMAGE_OVERLAY);

  const isAnnotating = viewerMode === "annotate";
  const isAnalyzeDialogOpen = viewerMode === "analyze";
  const isPromotePanelOpen = viewerMode === "promote";
  const isExtractionPanelOpen = viewerMode === "extract";
  const isSummarySharePanelOpen = viewerMode === "summaryShare";
  const isAdjustPointsPanelOpen = viewerMode === "adjustPoints";
  const isFocusedWorkspace = isFocusedWorkspaceMode(viewerMode, isAnalyzePanelDetached);
  const isFullscreen = isFocusedWorkspace;

  const { userPreferences, entityManager } = useSwivuBryntumContext();
  const [isRailCollapsed, setIsRailCollapsed] = useState(
    () => userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.fileDetailRailCollapsed) === "true",
  );
  const isRailFeatureMode = RAIL_FEATURE_MODES.includes(viewerMode);
  // Feature tabs always show the rail; Details respects the collapsed preference.
  const isRailExpanded = !isRailCollapsed || isRailFeatureMode;

  const [promoteAiSuggestions, setPromoteAiSuggestions] = useState<string[]>([]);
  const [promoteVisibilityWarnings, setPromoteVisibilityWarnings] = useState<string[]>([]);

  const rememberSummaryShareReturnMode = useCallback((from: FileViewerMode) => {
    if (from === "summaryShare") return;
    // Annotate is a separate shell; don't bounce Back into it from Share.
    summaryShareReturnModeRef.current = from === "annotate" ? "default" : from;
  }, []);

  const setAnalyzeImageOverlay = useCallback(
    (
      patch:
        | Partial<AnalyzeImageOverlayState>
        | ((prev: AnalyzeImageOverlayState) => AnalyzeImageOverlayState),
    ) => {
      setAnalyzeImageOverlayState((prev) =>
        typeof patch === "function" ? patch(prev) : { ...prev, ...patch },
      );
    },
    [],
  );

  /**
   * Back / close from a feature tab lands on Details. If the rail pref was
   * "collapsed" the rail was only force-shown by the feature mode, so clear the
   * pref — otherwise Details would render as an empty (collapsed) rail.
   */
  const returnToDetails = useCallback(() => {
    setViewerModeState("default");
    if (isRailCollapsed) {
      setIsRailCollapsed(false);
      userPreferences?.updateSetting(PERSISTENCE_STATE_KEYS.fileDetailRailCollapsed, "false");
    }
  }, [isRailCollapsed, userPreferences]);

  const setViewerMode = useCallback(
    (mode: FileViewerMode) => {
      // Re-selecting the active tab toggles it off → back to Details.
      if (mode === viewerMode) {
        returnToDetails();
        setIsAnalyzePanelDetached(false);
        setAnalyzeImageOverlayState(EMPTY_ANALYZE_IMAGE_OVERLAY);
        setPromoteAiSuggestions([]);
        setPromoteVisibilityWarnings([]);
        return;
      }
      if (mode === "summaryShare") {
        rememberSummaryShareReturnMode(viewerMode);
      }
      setViewerModeState(mode);
      if (mode !== "analyze") {
        setIsAnalyzePanelDetached(false);
        setAnalyzeImageOverlayState(EMPTY_ANALYZE_IMAGE_OVERLAY);
      }
      if (mode !== "promote") {
        setPromoteAiSuggestions([]);
        setPromoteVisibilityWarnings([]);
      }
      if (mode === "summaryShare") {
        setSummaryTab("share");
      }
      if (mode === "adjustPoints") {
        setSummaryTab("points");
      }
    },
    [viewerMode, returnToDetails, rememberSummaryShareReturnMode],
  );

  const setIsAnnotating = useCallback((value: boolean) => {
    setViewerModeState(value ? "annotate" : "default");
  }, []);

  const setIsAnalyzeDialogOpen = useCallback((openAnalyze: boolean) => {
    if (openAnalyze) {
      setViewerModeState("analyze");
      return;
    }
    returnToDetails();
    setIsAnalyzePanelDetached(false);
    setAnalyzeImageOverlayState(EMPTY_ANALYZE_IMAGE_OVERLAY);
  }, [returnToDetails]);

  const setIsPromotePanelOpen = useCallback((openPromote: boolean) => {
    if (openPromote) {
      setViewerModeState("promote");
      return;
    }
    returnToDetails();
    setPromoteAiSuggestions([]);
    setPromoteVisibilityWarnings([]);
  }, [returnToDetails]);

  const setIsExtractionPanelOpen = useCallback((openExtract: boolean) => {
    if (openExtract) {
      setViewerModeState("extract");
      return;
    }
    returnToDetails();
  }, [returnToDetails]);

  const handleCloseAnalyzePanel = useCallback(() => {
    returnToDetails();
    setIsAnalyzePanelDetached(false);
    setAnalyzeImageOverlayState(EMPTY_ANALYZE_IMAGE_OVERLAY);
  }, [returnToDetails]);

  const toggleAnalyzePanelDetach = useCallback(() => {
    setIsAnalyzePanelDetached((prev) => !prev);
  }, []);

  const handleNavigateToSharePermissions = useCallback(() => {
    setViewerModeState((prev) => {
      rememberSummaryShareReturnMode(prev);
      return "summaryShare";
    });
    setIsAnalyzePanelDetached(false);
    setAnalyzeImageOverlayState(EMPTY_ANALYZE_IMAGE_OVERLAY);
    setSummaryTab("share");
  }, [rememberSummaryShareReturnMode]);

  const handleOpenSummarySharePanel = useCallback(() => {
    setSummaryTab("share");
    setViewerMode("summaryShare");
  }, [setViewerMode]);

  const handleCloseSummarySharePanel = useCallback(() => {
    const returnMode = summaryShareReturnModeRef.current;
    summaryShareReturnModeRef.current = "default";
    if (returnMode === "default") {
      returnToDetails();
      return;
    }
    setViewerModeState(returnMode);
  }, [returnToDetails]);

  const handleOpenAdjustPointsPanel = useCallback(() => {
    if (!localFile || !project) return;
    if (viewerMode !== "adjustPoints") {
      setViewerMode("adjustPoints");
    }
  }, [localFile, project, viewerMode, setViewerMode]);

  const handleCloseAdjustPointsPanel = useCallback(() => {
    returnToDetails();
  }, [returnToDetails]);

  const handleCreateSpotIncentive = handleOpenAdjustPointsPanel;

  // Extraction panel state
  const [activeExtraction, setActiveExtraction] = useState<ExtractionType | null>(null);
  const [extractionStates, setExtractionStates] = useState<Partial<Record<ExtractionType, ExtractionState>>>({});
  const { runExtraction, cancelAllExtractions } = useDocumentExtraction();
  const { prefetchDocumentText, awaitDocumentText, cancelPrefetch } = useDocumentTextCache();

  // PDF text search
  const { pdfSearchText, searchInPdf: handleSearchInPdf, clearSearch } = usePdfTextSearch();

  // Check if file is an image to determine if we should load it
  const imageFile = useMemo(() => (isImageFileTyped(localFile) ? localFile : null), [localFile]);
  // Display URL comes only from GET /file/{id}/presigned-url — list url fields are not media srcs
  const { url: presignedFullUrl, isLoading: isPresignedUrlLoading } = useFileUrl(imageFile?.id);
  const imageUrlToLoad = presignedFullUrl ?? undefined;
  // Compute canEditFile directly
  const canEditFile = Boolean(localFile?.canEditGrant);

  const isExtractable = EXTRACTABLE_EXTENSIONS.has(getFileExtension(localFile?.name));

  // Custom hooks for business logic
  const {
    imageLoading: navigationImageLoading,
    showSpinner: navigationShowSpinner,
    displayedImageUrl,
    currentImageUrl,
    handleLoadComplete,
  } = useImageLoading(imageFile?.id, imageUrlToLoad);
  const imageLoading = navigationImageLoading || (!!imageFile && !imageUrlToLoad && isPresignedUrlLoading);
  const showSpinner = navigationShowSpinner || (!!imageFile && !imageUrlToLoad && isPresignedUrlLoading);
  const { hasPrevious, hasNext, handlePrevious: navigatePrevious, handleNext: navigateNext } = useFileNavigation(localFile, files, handleFileOpen);
  const showCollectionNav = canNavigateFileCollection(viewerMode, isAnalyzePanelDetached);
  const handlePrevious = useCallback(() => {
    if (!showCollectionNav) return;
    navigatePrevious();
  }, [showCollectionNav, navigatePrevious]);
  const handleNext = useCallback(() => {
    if (!showCollectionNav) return;
    void navigateNext();
  }, [showCollectionNav, navigateNext]);
  const { shouldShowAdjustPoints, handleAdjustPoints } = useFileSpotIncentive({
    entityRecord,
    project,
    editorHandler
  });

  const { version: marketingAssetVersion } = useSwivuStore({ storeId: STORE_KEYS.marketingAssetStore });
  const { version: insightsStoreVersion } = useSwivuStore({ storeId: STORE_KEYS.insightsStore });
  const { getInsights } = useFileInsights();
  const { copyEntityLink } = entityManager;

  // Feature-mode expansion is derived (isRailFeatureMode); no effect needed.
  useEffect(() => {
    if (file) {
      setLocalFile(file);
    }
  }, [file]);

  // Computed values
  const metadataRows = useMemo(() => formatFileMetadata(localFile), [localFile]);
  const shouldShowSpotIncentiveButton = shouldShowAdjustPoints(localFile);

  // Handlers
  const handleAnnotating = useCallback(() => {
    if (localFile && project && localFile.document != null) {
      setViewerModeState("annotate");
    }
  }, [localFile, project]);

  /** No-op — fullscreen is derived from viewer mode (Promote / Analyze / Adjust Points). */
  const toggleFullscreen = useCallback(() => {}, []);

  /** Feature tab that was open when the rail was collapsed — restored on expand. */
  const collapsedFromModeRef = useRef<FileViewerMode | null>(null);

  const toggleRailCollapsed = useCallback(() => {
    // While a feature tab is open the rail is forced visible — collapsing
    // returns to Details, stores the preference and remembers the tab so
    // expanding brings it back.
    if (isRailFeatureMode) {
      collapsedFromModeRef.current = viewerMode;
      setViewerModeState("default");
      setIsAnalyzePanelDetached(false);
      setAnalyzeImageOverlayState(EMPTY_ANALYZE_IMAGE_OVERLAY);
      setPromoteAiSuggestions([]);
      setPromoteVisibilityWarnings([]);
      setIsRailCollapsed(true);
      userPreferences?.updateSetting(
        PERSISTENCE_STATE_KEYS.fileDetailRailCollapsed,
        "true",
      );
      return;
    }
    const nextCollapsed = !isRailCollapsed;
    if (!nextCollapsed) {
      const restoreMode = collapsedFromModeRef.current;
      collapsedFromModeRef.current = null;
      if (restoreMode) setViewerModeState(restoreMode);
    }
    setIsRailCollapsed(nextCollapsed);
    userPreferences?.updateSetting(
      PERSISTENCE_STATE_KEYS.fileDetailRailCollapsed,
      String(nextCollapsed),
    );
  }, [isRailCollapsed, isRailFeatureMode, viewerMode, userPreferences]);

  // Any feature tab opened while collapsed (menu, toolbar) supersedes the remembered one.
  useEffect(() => {
    if (viewerMode !== "default") collapsedFromModeRef.current = null;
  }, [viewerMode]);

  const isFilePromoted = useMemo(() => {
    if (!project || !localFile) return false;
    const marketingAssetStore = project.marketingAssetStore;
    if (!marketingAssetStore) return false;
    return !!marketingAssetStore.findRecord("file", localFile.id, true);
  }, [project, localFile, marketingAssetVersion]);

  const isFileAnalyzed = useMemo(() => {
    if (!localFile?.id) return false;
    return getInsights(Number(localFile.id)).length > 0;
  }, [localFile?.id, getInsights, insightsStoreVersion]);

  const handleClosePromotePanel = useCallback(() => {
    returnToDetails();
    setPromoteAiSuggestions([]);
    setPromoteVisibilityWarnings([]);
  }, [returnToDetails]);

  // Builds the shared extraction request body for all document-ai endpoints.
  // Always "swivu": file_id is a Swivu File ID (API treats "companycam" as a CC asset id).
  const buildExtractionBody = useCallback((fileId: number): ExtractionRequestBody => {
    const selectedBoard = getSavedBoardSelection();
    const board_id = selectedBoard?.id ? Number(selectedBoard.id) : null;
    return { file_id: fileId, source: "swivu", board_id };
  }, []);

  // Extraction handlers
  const handleRunExtraction = useCallback((type: ExtractionType) => {
    if (!localFile) return;

    // Fire-and-forget: each type manages its own state slice independently
    const run = async () => {
      setExtractionStates((prev) => ({
        ...prev,
        [type]: { loading: true, loadingPhase: "analyzing", error: null, results: prev[type]?.results ?? null },
      }));

      try {
        await awaitDocumentText();

        setExtractionStates((prev) => ({
          ...prev,
          [type]: { ...prev[type]!, loadingPhase: "extracting" },
        }));

        const results = await runExtraction(type, buildExtractionBody(localFile.id as number));
        if (results) {
          setExtractionStates((prev) => ({
            ...prev,
            [type]: { loading: false, loadingPhase: null, error: null, results },
          }));
        }
      } catch (error: any) {
        const message =
          error?.response?.data?.error ??
          error?.message ??
          "Extraction failed";
        setExtractionStates((prev) => ({
          ...prev,
          [type]: { loading: false, loadingPhase: null, error: typeof message === "string" ? message : String(message), results: null },
        }));
      }
    };

    run();
  }, [localFile, runExtraction, awaitDocumentText, buildExtractionBody]);

  const handleRunAllExtractions = useCallback(() => {
    EXTRACTION_OPERATIONS.forEach((op) => {
      const state = extractionStates[op.type];
      if (!state?.loading && !state?.results) {
        handleRunExtraction(op.type);
      }
    });
  }, [handleRunExtraction, extractionStates]);

  const handleViewExtraction = useCallback((type: ExtractionType) => {
    const state = extractionStates[type];
    if (state?.results || state?.loading) {
      // Already has results or is actively loading — navigate to the results view
      setActiveExtraction(type);
    } else {
      handleRunExtraction(type);
    }
  }, [extractionStates, handleRunExtraction]);

  const handleCloseExtractionPanel = useCallback(() => {
    cancelAllExtractions();
    clearSearch();
    returnToDetails();
    setActiveExtraction(null);
    setExtractionStates({});
  }, [cancelAllExtractions, clearSearch, returnToDetails]);

  const handleOpenAnalyzePanel = useCallback(() => {
    setViewerMode("analyze");
  }, [setViewerMode]);

  const handleOpenPromotePanel = useCallback(async () => {
    setViewerMode("promote");
  }, [setViewerMode]);

  const handleBackToOperationList = useCallback(() => {
    clearSearch();
    setActiveExtraction(null);
    // Leave extractionStates intact so parallel extractions keep running
  }, [clearSearch]);

  const initialModeAppliedRef = useRef(false);

  // Reset side panels when navigating between files (keep modal open)
  useEffect(() => {
    if (!initialModeAppliedRef.current) return;
    setViewerModeState("default");
    collapsedFromModeRef.current = null;
    setActiveExtraction(null);
    setExtractionStates({});
    cancelAllExtractions();
    clearSearch();
    cancelPrefetch();
    setPromoteAiSuggestions([]);
    setPromoteVisibilityWarnings([]);
    setIsAnalyzePanelDetached(false);
    setAnalyzeImageOverlayState(EMPTY_ANALYZE_IMAGE_OVERLAY);
  // eslint-disable-next-line -- reset only on file id change after initial open
  }, [localFile?.id]);

  // Apply initial mode once when modal opens
  useEffect(() => {
    if (open) {
      const mode = autoOpenPromotePanel ? "promote" : initialViewerMode;
      setViewerModeState(mode);
      setActiveTab("review");
      initialModeAppliedRef.current = true;
    } else {
      initialModeAppliedRef.current = false;
      collapsedFromModeRef.current = null;
    }
  }, [open, autoOpenPromotePanel, initialViewerMode]);

  // Prefetch document text cache when extraction panel opens for extractable files
  useEffect(() => {
    if (isExtractionPanelOpen && localFile?.id && isExtractable) {
      prefetchDocumentText(buildExtractionBody(localFile.id as number));
    }
  }, [isExtractionPanelOpen, localFile?.id, isExtractable, prefetchDocumentText, buildExtractionBody]);

  // Add keyboard listener for navigation
  useEffect(() => {
    if (!open || !showCollectionNav) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        // Don't handle navigation if user is typing in an input field
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && (
          activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.hasAttribute("contenteditable")
        );

        if (isInputFocused) {
          return; // Let the input handle the arrow keys normally
        }

        e.preventDefault();
        if (e.key === "ArrowLeft") {
          handlePrevious();
        } else {
          handleNext();
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [open, showCollectionNav, handlePrevious, handleNext]);

  const value: FileDetailModalContextValue = {
    // File state
    file: localFile,
    files,
    imageFile,
    imageUrlToLoad,

    // Image loading state
    imageLoading,
    showSpinner,
    displayedImageUrl,
    currentImageUrl,
    handleLoadComplete,

    // Navigation state
    hasPrevious,
    hasNext,
    handlePrevious,
    handleNext,

    // File permissions
    canEditFile,
    handleFileDownload,
    deleteFile,

    // Spot incentive state
    shouldShowAdjustPoints,
    handleAdjustPoints,

    // Exclusive viewer mode
    viewerMode,
    setViewerMode,

    // UI state
    activeTab,
    setActiveTab,
    summaryTab,
    setSummaryTab,
    isAnnotating,
    setIsAnnotating,
    isFocusedWorkspace,
    isFullscreen,
    toggleFullscreen,
    isRailCollapsed,
    isRailExpanded,
    toggleRailCollapsed,
    isAnalyzeDialogOpen,
    setIsAnalyzeDialogOpen,
    handleOpenAnalyzePanel,
    handleCloseAnalyzePanel,
    isFileAnalyzed,
    isAnalyzePanelDetached,
    toggleAnalyzePanelDetach,
    handleNavigateToSharePermissions,
    isSummarySharePanelOpen,
    handleOpenSummarySharePanel,
    handleCloseSummarySharePanel,
    isAdjustPointsPanelOpen,
    handleOpenAdjustPointsPanel,
    handleCloseAdjustPointsPanel,
    analyzeImageOverlay,
    setAnalyzeImageOverlay,

    // Modal state
    open,
    setOpen,

    // Computed values
    metadataRows,
    shouldShowSpotIncentiveButton,

    // External dependencies
    entityRecord,
    project,
    swivuAppGrants,
    editorHandler,
    handleFileOpen,

    // Handlers
    handleAnnotating,
    handleCreateSpotIncentive,

    // Extraction panel state
    isExtractable,
    isExtractionPanelOpen,
    setIsExtractionPanelOpen,
    activeExtraction,
    extractionStates,
    handleRunExtraction,
    handleRunAllExtractions,
    handleViewExtraction,
    handleCloseExtractionPanel,
    handleBackToOperationList,

    // PDF search
    pdfSearchText,
    handleSearchInPdf,
    clearPdfSearch: clearSearch,

    // Entity link
    copyEntityLink,

    // Promote panel state
    isPromotePanelOpen,
    setIsPromotePanelOpen,
    handleOpenPromotePanel,
    isFilePromoted,
    handleClosePromotePanel,
    promoteAiSuggestions,
    setPromoteAiSuggestions,
    promoteVisibilityWarnings,
    setPromoteVisibilityWarnings,
  };

  return <FileDetailModalContext.Provider value={value}>{children}</FileDetailModalContext.Provider>;
};

export const useFileDetailModal = () => {
  const context = useContext(FileDetailModalContext);
  if (context === undefined) {
    throw new Error("useFileDetailModal must be used within a FileDetailModalProvider");
  }
  return context;
};

/** Returns null outside File Detail (e.g. My Media analyze modal). */
export const useOptionalFileDetailModal = () => useContext(FileDetailModalContext);
